import { IconButton } from "@mui/material";
import { Grid, Loader, Modal, Typography } from "../../../components";
import { ContentCopy } from "@mui/icons-material";
import { Fragment, useCallback, useEffect, useState } from "react";
import { formatCPF, formatDate } from "../../../utils";
import { enqueueSnackbar } from "notistack";
import { useAxios } from "../../../hooks";
import VisibilityIcon from '@mui/icons-material/Visibility';

interface AccountModalBidsProps {
  accountModalVisibility: boolean;
  accountModalVisibilityControls: {
    setFalse: () => void;
  };
  loading: boolean;
  bid: {
      account_id: string;
      account: {
        is_membership_active: boolean;
        login: string;
        holder_name: string;
        holder_email: string;
        holder_cpf: string;
        holder_birthday: Date;
      };
  };
}

export const AccountModalBids = ({
  accountModalVisibility,
  accountModalVisibilityControls,
  loading,
  bid
}: AccountModalBidsProps) => {
  const [passwordData, setPasswordData] = useState<null | {
    password: string;
    secondary_password: string;
  }>(null);

  const [password, fetchPassword] = useAxios("/account", {
    manual: true,
  });

  const handleRevealPassword = useCallback(async () => {
    const passwordResponse = await fetchPassword({
      url: `/account/${bid?.account_id}/password`,
      method: "GET",
    });

    setPasswordData(passwordResponse.data);
  }, [fetchPassword, bid?.account_id]);

  useEffect(() => {
    if (!accountModalVisibility) {
      setPasswordData(null);
    }
  }, [accountModalVisibility]);

  return (
    <Modal
      open={accountModalVisibility}
      onClose={accountModalVisibilityControls.setFalse}
      title="Dados da Conta"
    >
      {loading || password.loading ? (
        <div style={{ textAlign: "center", width: 600 }}>
          <Loader />
        </div>
      ) : (
        <Grid container spacing={2} width={600}>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">Clube Ativo:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {bid?.account?.is_membership_active ? "Sim" : "Não"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">Login:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {bid?.account?.login}
                  <IconButton
                    size="small"
                    sx={{ ml: 1 }}
                    color="primary"
                    onClick={() => {
                      navigator?.clipboard?.writeText(
                        bid?.account?.login
                      );
                      enqueueSnackbar("Copiado!", { variant: "success" });
                    }}
                  >
                    <ContentCopy />
                  </IconButton>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">Senha:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {passwordData ? (
                    <Fragment>
                      {passwordData?.password}
                      <IconButton
                        size="small"
                        sx={{ ml: 1 }}
                        color="primary"
                        onClick={() => {
                          navigator?.clipboard?.writeText(
                            passwordData?.password
                          );
                          enqueueSnackbar("Copiado!", { variant: "success" });
                        }}
                      >
                        <ContentCopy />
                      </IconButton>
                    </Fragment>
                  ) : (
                    <div
                      style={{ cursor: "pointer" }}
                      onClick={handleRevealPassword}
                    >
                      <VisibilityIcon fontSize="inherit" />
                    </div>
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">Contrassenha:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {passwordData ? (
                    <Fragment>
                      {passwordData?.secondary_password ?? "-"}
                      {passwordData?.secondary_password && (
                        <IconButton
                          size="small"
                          sx={{ ml: 1 }}
                          color="primary"
                          onClick={() => {
                            navigator?.clipboard?.writeText(
                              passwordData?.secondary_password
                            );
                            enqueueSnackbar("Copiado!", { variant: "success" });
                          }}
                        >
                          <ContentCopy />
                        </IconButton>
                      )}
                    </Fragment>
                  ) : (
                    <div
                      style={{ cursor: "pointer" }}
                      onClick={handleRevealPassword}
                    >
                      <VisibilityIcon fontSize="inherit" />
                    </div>
                  )}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">Nome do Titular:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {bid?.account?.holder_name}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">E-mail do Titular:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {bid?.account?.holder_email}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">CPF do Titular:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {formatCPF(bid?.account?.holder_cpf || "")}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container direction="row">
              <Grid item xs={6}>
                <Typography fontWeight="bold">
                  Data de Nasc. do Titular:
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography textAlign="end">
                  {formatDate(bid?.account?.holder_birthday || "")}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Modal>
  );
};
